// A project's room thread (Brief 4 / P8) — every level of the descent is a chat.
// A project has its OWN general chat (like the Global room, but scoped to one
// project): find the caller's existing `group` thread titled `project:<id>`, or
// create it once. The same threads API every chat uses — no mock, no new
// plumbing. Mirrors `useGlobalRoom`, including the oldest-wins dedup so every
// surface converges on ONE room per project.

import { useEffect, useRef, useState } from "react";
import { createThread, listThreads } from "../../api/client";

/** The well-known title of a project's room (a `group` thread) — one per project
 * id, so the room is stable and shared across surfaces. */
export function projectRoomTitle(projectId: number): string {
  return `project:${projectId}`;
}

export function useProjectRoom(projectId: number | null): {
  threadId: number | null;
  loading: boolean;
  error: string;
} {
  const [threadId, setThreadId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Resolve once PER project id — guards React StrictMode's double-mount from
  // creating two rooms for the same project, while still re-resolving when you
  // switch projects.
  const startedFor = useRef<number | null>(null);

  useEffect(() => {
    if (projectId == null) {
      setThreadId(null);
      setLoading(false);
      return;
    }
    if (startedFor.current === projectId) return;
    startedFor.current = projectId;
    let alive = true;
    setThreadId(null);
    setLoading(true);
    setError("");
    const title = projectRoomTitle(projectId);
    (async () => {
      try {
        const { threads } = await listThreads();
        const canonical = threads
          .filter((t) => t.kind === "group" && t.title === title)
          .sort((a, b) => a.id - b.id)[0];
        const thread = canonical ?? (await createThread("group", title, []));
        if (alive) setThreadId(thread.id);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId]);

  return { threadId, loading, error };
}
